import { Form, useForm } from 'react-hook-form';
import type { PollData, Vote } from '@library/types/polls';
import { useCallback, useState } from 'react';
import { Combobox } from '@library/components/selects';
import type { ComboboxOption } from '@library/components/selects';
import { ConfirmationModal } from '@library/components/modals/ConfirmationModal';
import { FormActionButtons } from '@library/components/buttons';
import { FormFieldController } from '@library/components/forms';
import { StyledCard } from '@bo-components/common/StyledComponents';
import StyledModal from '@bo-components/common/StyledModal';
import { useSetPollWinner } from '@bo-hooks/polls/useSetPollWinner';

export interface WinnerSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  poll: PollData;
}

interface Winner {
  winner: ComboboxOption | null;
}

const DEFAULT_WINNER_DATA = {
  winner: undefined
};

export const WinnerSelectModal = ({
  isOpen,
  onClose,
  onSubmit,
  poll
}: WinnerSelectModalProps) => {
  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false);

  const methods = useForm<Winner>({ defaultValues: DEFAULT_WINNER_DATA });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = methods;

  const options = (poll.options || []).map(
    (option: Vote): ComboboxOption => ({
      label: option?.answer || '',
      value: option?.id || '',
      isDisabled: false
    })
  );

  const { isLoading, createPollWinnerError, handleSetPollWinner, isSuccess } =
    useSetPollWinner();

  const handleSubmitForm = useCallback(
    async (formData: Winner) => {
      const { winner } = formData;
      if (!winner?.value) return;
      try {
        await handleSetPollWinner(winner.value as string);
        reset(DEFAULT_WINNER_DATA);
        onSubmit();
      } catch {
        reset(DEFAULT_WINNER_DATA);
      }
      setShowConfirmationScreen(false);
    },
    [handleSetPollWinner, reset, onSubmit]
  );

  const handleClose = useCallback(() => {
    reset(DEFAULT_WINNER_DATA);
    setShowConfirmationScreen(false);
    onClose();
  }, [reset, setShowConfirmationScreen, onClose]);

  const renderFormComponent = useCallback(
    (value: ComboboxOption, onChange: (value: ComboboxOption) => void) => {
      return (
        <Combobox
          options={options}
          onChange={(selected) => {
            if (selected) onChange(selected);
          }}
          value={value}
          placeholder="Select a winner..."
          isMulti={false}
          id={poll.id}
        />
      );
    },
    [options, poll]
  );

  return (
    <div>
      {showConfirmationScreen ? (
        <ConfirmationModal
          confirmationCopy="Confirm"
          handleConfirm={handleSubmit(handleSubmitForm)}
          label="Set Winner"
          onCancelModal={() => setShowConfirmationScreen(false)}
          isOpen={isOpen}
        >
          <p>Are you sure? This action can&#39;t be undone!</p>
        </ConfirmationModal>
      ) : (
        <StyledModal
          isOpen={isOpen}
          onClose={onClose}
          bigTitle="Set Winner"
          testId="poll-winner-modal"
        >
          <Form control={control}>
            <>
              <StyledCard title={poll?.question}>
                <FormFieldController name="winner" control={control} required>
                  {({ value, onChange }) =>
                    renderFormComponent(value as ComboboxOption, onChange)
                  }
                </FormFieldController>
              </StyledCard>
              <div className="border-soFresh-140 bg-soFresh-100 sticky bottom-0 flex items-center justify-end gap-4 border-t border-solid py-3 pr-5">
                {isSuccess || createPollWinnerError ? (
                  <p>{isSuccess ? `Winner Set!` : `Something went wrong.`}</p>
                ) : null}
                <FormActionButtons
                  className="flex items-center justify-end gap-4 border-none py-3 pr-5"
                  primary={{
                    onClick: () => {
                      setShowConfirmationScreen(true);
                    },
                    disabled: !isValid || isLoading || isSuccess
                  }}
                  secondary={{ onClick: handleClose }}
                />
              </div>
            </>
          </Form>
        </StyledModal>
      )}
    </div>
  );
};
